import supabase from './supabase';

// Function to create a new route
export async function createRoute(routeData) {
  const { data, error } = await supabase
    .from('routes')
    .insert([routeData])
    .select()
    .single();
  if (error) {
    console.error('Supabase insert error:', error); // Log the real error
    throw error;
  }
  return data;
}

// Function to fetch all routes for a user
export async function fetchRoutes(userId) {
  try {
    const { data, error } = await supabase
      .from('routes')
      .select('*')
      .eq('user_id', userId);
    if (error) {
      console.log('[DEBUG] Routes table not found or error:', error);
      // Return empty array instead of throwing error
      return [];
    }
    return data || [];
  } catch (error) {
    console.log('[DEBUG] Routes table does not exist, returning empty array');
    // Return empty array if table doesn't exist
    return [];
  }
}

// Privacy-aware fetch for viewing another user's profile.
// Returns public routes by owner plus routes where viewer is a collaborator.
export async function fetchAccessibleRoutes(ownerUserId, viewerUserId) {
  if (!ownerUserId) return [];
  try {
    // Debug auth/session for visibility issues
    try {
      const { data: { user: sessionUser } } = await supabase.auth.getUser();
      console.log('[routes] fetchAccessibleRoutes auth check', {
        sessionUserId: sessionUser?.id,
        ownerUserId,
        viewerUserId,
        isOwnProfile: viewerUserId && ownerUserId === viewerUserId
      });
    } catch (e) {
      console.warn('[routes] fetchAccessibleRoutes auth check failed', e);
    }

    // If viewing own profile, just use owner query
    if (viewerUserId && ownerUserId === viewerUserId) {
      const { data, error } = await supabase
        .from('routes')
        .select('*')
        .eq('user_id', ownerUserId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }

    // Public routes by owner
    const { data: publicRoutes, error: pubErr } = await supabase
      .from('routes')
      .select('*')
      .eq('user_id', ownerUserId)
      .eq('is_private', false)
      .order('created_at', { ascending: false });
    if (pubErr) throw pubErr;

    // If no viewer, return public only
    if (!viewerUserId) return publicRoutes || [];

    // Collaborator routes for viewer (join via subquery)
    const { data: collabRoutes, error: colErr } = await supabase
      .from('routes')
      .select('*')
      .in('id', supabase
        .from('route_collaborators')
        .select('route_id')
        .eq('user_id', viewerUserId)
      );
    // If supabase can't use a subselect directly, fall back to two-step:
    if (colErr) {
      const { data: rcRows, error: rcErr } = await supabase
        .from('route_collaborators')
        .select('route_id')
        .eq('user_id', viewerUserId);
      if (rcErr) throw rcErr;
      const routeIds = (rcRows || []).map(r => r.route_id);
      if (routeIds.length === 0) return publicRoutes || [];
      const { data: routesData, error: routesErr } = await supabase
        .from('routes')
        .select('*')
        .in('id', routeIds);
      if (routesErr) throw routesErr;
      return dedupeById([...(publicRoutes || []), ...(routesData || [])]);
    }

    return dedupeById([...(publicRoutes || []), ...(collabRoutes || [])]);
  } catch (error) {
    console.error('[routes] fetchAccessibleRoutes error:', error);
    return [];
  }
}

function dedupeById(rows) {
  const map = new Map();
  for (const r of rows || []) map.set(r.id, r);
  return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

// Function to update a route
export async function updateRoute(routeId, updatedData) {
  const { data, error } = await supabase
    .from('routes')
    .update(updatedData)
    .eq('id', routeId);
  if (error) throw error;
  return data;
}

// Function to delete a route
export async function deleteRoute(routeId) {
  const { data, error } = await supabase
    .from('routes')
    .delete()
    .eq('id', routeId);
  if (error) throw error;
  return data;
} 

// Fetch all points for a given route
export async function fetchRoutePoints(routeId) {
  try {
    const { data, error } = await supabase
      .from('route_points')
      .select('*')
      .eq('route_id', routeId)
      .order('point_order', { ascending: true });
    if (error) {
      console.log('[DEBUG] Route points table not found or error:', error);
      // Return empty array instead of throwing error
      return [];
    }
    return data || [];
  } catch (error) {
    console.log('[DEBUG] Route points table does not exist, returning empty array');
    // Return empty array if table doesn't exist
    return [];
  }
} 